/**
 * Relayer URL resolution — a LEAF module.
 *
 * Chain configs call `resolveRelayerUrl()` at module scope, so this file must
 * not import anything that imports chain configs back (`CHAIN_CONFIGS` lives
 * behind `./validate`). Keeping the cycle broken here is load-bearing: a cycle
 * would leave `resolveRelayerUrl` undefined while the configs initialise.
 */
import { ChainIdEnum } from "../../enums/chain";
import UPSTREAMS from "./upstreams.json";

/** Same-origin path; proxied to the chain's upstream in dev and by nginx in prod. */
export const SAME_ORIGIN_RELAYER_PATH = "/relayer";

/** The real relayer origin for a chain, or `undefined` when none is known. */
export const getRelayerUpstream = (
  chainId: ChainIdEnum
): string | undefined =>
  (UPSTREAMS as Record<string, string>)[String(chainId)] || undefined;

/**
 * The URL the app posts orders to.
 *
 * Order of preference:
 *   1. `REACT_APP_RELAYER_URL` — the explicit per-deployment override.
 *   2. The chain's known upstream — the relayer that actually serves this chain.
 *   3. The same-origin path, for a chain with no upstream recorded.
 *
 * Step 2 exists because step 1 silently vanished in production. `.env.monad`
 * sets the override, but CRA only auto-loads `.env`, `.env.local` and
 * `.env.production` — `.env.monad` applies ONLY when building through
 * `scripts/build-chain.sh` (see its header, which says exactly this). A Monad
 * deployment built with a plain `npm run build` therefore had no override, fell
 * through to `/relayer`, and — where nginx does not proxy that path — got the
 * app's own `index.html` back with a 200. Every perp order on that deployment
 * failed, and the failure looked like a JSON parse bug in the order code.
 *
 * Falling back to the recorded upstream removes the dependency on anyone
 * remembering the build command. Calling it cross-origin is safe and verified:
 * Monad's relayer returns `Access-Control-Allow-Origin: https://app.marginx.trade`
 * (with a 204 preflight allowing GET/POST and Content-Type), DogeOS returns `*`,
 * and Arc's upstream is the same host that serves the Arc app, so it is
 * same-origin there anyway. The previous comment here asserted the opposite —
 * that a browser "must never" call a relayer cross-origin — and that assumption
 * is what replaced a working direct URL with a path nothing served.
 */
export const resolveRelayerUrl = (chainId?: ChainIdEnum): string => {
  if (process.env.REACT_APP_RELAYER_URL) return process.env.REACT_APP_RELAYER_URL;

  // In development the same-origin path is CORRECT and the upstream is not:
  // `src/setupProxy.js` forwards `/relayer` to this chain's upstream
  // server-side, which is what makes a relayer with no CORS headers usable from
  // localhost at all. Arc's relayer sets none — probed 2026-09-05, no
  // `Access-Control-Allow-Origin` for any origin — so calling it directly from
  // `localhost:3000` is blocked by the browser. The proxy is the only way.
  if (process.env.NODE_ENV === "development") return SAME_ORIGIN_RELAYER_PATH;

  // In production the upstream is correct and the path is a gamble, because it
  // depends on the host proxying `/relayer` — which app.marginx.trade does not.
  // Verified reachable for each chain that has an upstream:
  //   Monad   relayer.marginx.trade    -> ACAO: https://app.marginx.trade (204 preflight)
  //   DogeOS  perp-api.muchfi.xyz      -> ACAO: *
  //   Arc     arc-testnet.realtime.exchange -> same host that serves the Arc app,
  //           so the absolute URL is same-origin there and needs no CORS at all.
  return (
    (chainId !== undefined ? getRelayerUpstream(chainId) : undefined) ||
    SAME_ORIGIN_RELAYER_PATH
  );
};
